<template>
  <div class="booking">
    <h3 class="booking__title">Выберите время встречи-разбора</h3>
    <p class="booking__hint">30 минут · время московское (МСК)</p>

    <div v-if="loading" class="booking__status">Загружаем свободные окна…</div>

    <div v-else-if="confirmed" class="booking__confirmed">
      Встреча назначена на <strong>{{ confirmed }}</strong>.<br/>
      Мы позвоним или напишем вам для подтверждения.
    </div>

    <div v-else-if="loadError || !days.length" class="booking__status">
      Свободных окон сейчас нет — мы сами свяжемся с вами в течение рабочего дня.
      Или напишите на <a href="mailto:info@candb.pro">info@candb.pro</a>.
    </div>

    <template v-else>
      <div class="booking__days">
        <button
          v-for="day in days"
          :key="day.key"
          type="button"
          class="booking__chip"
          :class="{ 'booking__chip--active': selectedDay === day.key }"
          @click="selectedDay = day.key; selectedSlot = null"
        >{{ day.label }}</button>
      </div>
      <div class="booking__slots">
        <button
          v-for="slot in currentSlots"
          :key="slot.utc"
          type="button"
          class="booking__chip booking__chip--time"
          :class="{ 'booking__chip--active': selectedSlot && selectedSlot.utc === slot.utc }"
          @click="selectedSlot = slot"
        >{{ slot.time }}</button>
      </div>
      <p v-if="conflict" class="booking__conflict">Это время только что заняли — выберите другое.</p>
      <base-button
        v-if="selectedSlot"
        size="md"
        style="width:100%; justify-content:center; margin-top:16px; white-space:normal; text-align:center;"
        :disabled="booking"
        @click="book"
      >{{ booking ? 'Бронируем…' : 'Забронировать ' + selectedLabel }}</base-button>
    </template>
  </div>
</template>

<script>
import BaseButton from './ui/BaseButton.vue'
import { reachGoal } from '../analytics'

// МСК = UTC+3 без переходов на летнее время (фиксировано с 2014 года)
const MSK_OFFSET_MS = 3 * 60 * 60 * 1000
const SLOT_MS = 30 * 60 * 1000
const HOUR_START = 10
const HOUR_END = 18
const LEAD_MS = 3 * 60 * 60 * 1000
const HORIZON_DAYS = 14

const TOKEN = process.env.VUE_APP_BOOKING_TOKEN || ''
const COMPANY = process.env.VUE_APP_BOOKING_COMPANY || ''

export function bookingConfigured() {
  return Boolean(TOKEN && COMPANY)
}

function uuid4() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
  const b = new Uint8Array(16)
  window.crypto.getRandomValues(b)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, x => x.toString(16).padStart(2, '0'))
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`
}

export default {
  name: 'BookingPicker',
  components: { BaseButton },
  props: {
    name: { type: String, default: '' },
    phone: { type: String, default: '' },
    email: { type: String, default: '' },
    comment: { type: String, default: '' },
    apiBase: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      days: [],          // [{key, label, slots: [{utc, iso, time, human}]}]
      selectedDay: null,
      selectedSlot: null,
      booking: false,
      conflict: false,
      confirmed: '',
    }
  },
  computed: {
    currentSlots() {
      const day = this.days.find(d => d.key === this.selectedDay)
      return day ? day.slots : []
    },
    selectedLabel() {
      return this.selectedSlot ? this.selectedSlot.human : ''
    },
  },
  created() {
    this.refresh()
  },
  methods: {
    getUtm() {
      try {
        return JSON.parse(localStorage.getItem('utm_params') || '{}')
      } catch (e) {
        return {}
      }
    },
    async fetchBusy() {
      const url = this.apiBase + 'tasks/api/objects/search/?' + new URLSearchParams({
        type: 'meeting', company: COMPANY, status_not: '0', limit: '1000',
      })
      const res = await fetch(url, { headers: { Authorization: 'Step ' + TOKEN } })
      if (!res.ok) throw new Error('search failed')
      const data = await res.json()
      const busy = new Set()
      ;(data.results || []).forEach(o => {
        if (o.date_start) busy.add(new Date(o.date_start).getTime())
      })
      return busy
    },
    buildGrid(busy) {
      const now = Date.now()
      const minStart = now + LEAD_MS
      // Сдвинутая эпоха: UTC-поля Date(ts + offset) = московские поля момента ts
      const mskNowShifted = new Date(now + MSK_OFFSET_MS)
      const midnightShifted = Date.UTC(
        mskNowShifted.getUTCFullYear(), mskNowShifted.getUTCMonth(), mskNowShifted.getUTCDate())
      const days = []
      for (let d = 0; d < HORIZON_DAYS; d++) {
        const dayShifted = midnightShifted + d * 24 * 60 * 60 * 1000
        const wd = new Date(dayShifted).getUTCDay()
        if (wd === 0 || wd === 6) continue // только будни
        const slots = []
        for (let m = HOUR_START * 60; m < HOUR_END * 60; m += 30) {
          const utc = dayShifted - MSK_OFFSET_MS + m * 60 * 1000
          if (utc < minStart || busy.has(utc)) continue
          const t = new Date(dayShifted + m * 60 * 1000)
          const time = `${String(t.getUTCHours()).padStart(2, '0')}:${String(t.getUTCMinutes()).padStart(2, '0')}`
          slots.push({
            utc,
            iso: new Date(utc).toISOString(),
            time,
            human: new Date(dayShifted).toLocaleDateString('ru-RU', {
              timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'long',
            }) + ', ' + time + ' МСК',
          })
        }
        if (!slots.length) continue
        days.push({
          key: dayShifted,
          label: new Date(dayShifted).toLocaleDateString('ru-RU', {
            timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short',
          }),
          slots,
        })
      }
      return days
    },
    async refresh() {
      this.loading = true
      this.loadError = false
      try {
        const busy = await this.fetchBusy()
        this.days = this.buildGrid(busy)
        if (!this.days.find(d => d.key === this.selectedDay)) {
          this.selectedDay = this.days.length ? this.days[0].key : null
          this.selectedSlot = null
        }
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    meetingTransaction(slot) {
      const objPk = uuid4()
      const obj = { pk: objPk, type: 'meeting' }
      const rel = (name, type, key, value) => ({
        pk: uuid4(), name, type, object: obj, [key]: value,
      })
      const stopIso = new Date(slot.utc + SLOT_MS).toISOString()
      const descLines = []
      if (this.comment) descLines.push(this.comment)
      const utm = this.getUtm()
      Object.keys(utm).forEach(k => descLines.push(k + ': ' + utm[k]))
      const relations = [
        rel('type', 'type', 'strings', [{ value: 'meeting' }]),
        rel('name', 'name', 'strings', [{ value: 'Встреча-разбор: ' + (this.name || 'с сайта') }]),
        rel('company', 'parents', 'parents', [{ value: { pk: COMPANY } }]),
        rel('date_start', 'date', 'dates', [{ value: slot.iso }]),
        rel('date_stop', 'date', 'dates', [{ value: stopIso }]),
        rel('ordering', 'int', 'ints', [{ value: Date.now().toString() }]),
      ]
      if (this.phone) relations.push(rel('phone', 'phone', 'strings', [{ value: this.phone }]))
      if (this.email) relations.push(rel('email', 'email', 'strings', [{ value: this.email }]))
      if (descLines.length) relations.push(rel('description', 'text', 'texts', [{ value: descLines.join('\n') }]))
      return { pk: uuid4(), date: new Date().toISOString(), relations }
    },
    async book() {
      if (!this.selectedSlot || this.booking) return
      reachGoal('booking_click')
      this.booking = true
      this.conflict = false
      const slot = this.selectedSlot
      try {
        // Контрольная перечитка занятости прямо перед бронью (двое на один слот)
        const busy = await this.fetchBusy()
        if (busy.has(slot.utc)) {
          this.conflict = true
          this.days = this.buildGrid(busy)
          this.selectedSlot = null
          if (!this.days.find(d => d.key === this.selectedDay)) {
            this.selectedDay = this.days.length ? this.days[0].key : null
          }
          return
        }
        const res = await fetch(this.apiBase + 'tasks/api/wtransactions/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Step ' + TOKEN },
          body: JSON.stringify(this.meetingTransaction(slot)),
        })
        if (!res.ok) throw new Error('booking failed')
        this.confirmed = slot.human
        reachGoal('booking_confirmed')
        // Telegram-уведомление — существующим API лидов, fire-and-forget
        fetch(this.apiBase + 'core/api/leads/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'demo',
            name: this.name || 'Бронь с сайта',
            email: this.email,
            phone: this.phone,
            comment: '📅 Забронирована встреча-разбор: ' + slot.human,
            utm: this.getUtm(),
          }),
        }).catch(() => {})
      } catch (e) {
        this.loadError = false
        this.conflict = false
        // Бронь не прошла (сеть/сервер) — оставляем выбор, даём попробовать ещё раз
        this.$emit('error')
        this.refresh()
      } finally {
        this.booking = false
      }
    },
  },
}
</script>

<style scoped>
.booking {
  margin-top: 20px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
  background: white;
  /* внутри grid-колонки (.demo-layout) у детей min-width:auto — без этого
     ряд чипов с nowrap распирает колонку шире экрана на мобильных */
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}
.booking__title { font-size: 1.125rem; font-weight: 700; margin-bottom: 4px; }
.booking__hint { font-size: 0.8125rem; color: var(--gray); margin-bottom: 16px; }
.booking__status { font-size: 0.9375rem; color: var(--gray); line-height: 1.55; }
.booking__status a { color: var(--primary); }
.booking__days {
  display: flex; gap: 8px; overflow-x: auto; padding-bottom: 8px; margin-bottom: 12px;
  -webkit-overflow-scrolling: touch;
}
.booking__slots {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(64px, 1fr)); gap: 8px;
}
.booking__chip {
  border: 1px solid var(--border);
  background: white;
  border-radius: var(--radius-sm);
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--dark);
  padding: 8px 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: var(--transition);
}
.booking__chip:hover { border-color: var(--primary); color: var(--primary); }
.booking__chip--active {
  background: var(--primary);
  border-color: var(--primary);
  color: white;
}
.booking__conflict {
  margin-top: 12px;
  background: #FFFBEB; border: 1px solid #FDE68A; color: #92400E;
  padding: 10px 14px; border-radius: var(--radius-sm); font-size: 0.875rem;
}
.booking__confirmed {
  background: #ECFDF5; border: 1px solid #A7F3D0; color: #065F46;
  padding: 12px 14px; border-radius: var(--radius-sm); font-size: 0.9375rem;
  line-height: 1.55;
}
</style>
